import type { OrderStatus, PaymentStatus } from '@/features/orders/api';

/*
  Reports' page tabs, the Transactions (ALS-86) and Clients (ALS-87) tabs' views, the Client
  heatmap's (ALS-88) windows, Brand intelligence's states (ALS-91), App usage's (ALS-160) windows,
  and their enums, one place each. The tab and view values ride the URL; the enum values are the
  API's names.
*/

export const REPORT_TAB = {
    Overview: 'overview',
    Transactions: 'transactions',
    Clients: 'clients',
    /** "heatmap", as the prototype's own tab names it. Before Brand intelligence, as the prototype orders them. */
    Heatmap: 'heatmap',
    Brands: 'brands',
    /** "app", as the prototype's own address names it: reports.html?tab=app. */
    AppUsage: 'app',
} as const;

export type ReportTab = (typeof REPORT_TAB)[keyof typeof REPORT_TAB];

export const REPORT_TABS = Object.values(REPORT_TAB) as [ReportTab, ...ReportTab[]];

/** The Transactions sub-tabs, in the order the tab draws them: the prototype's Orders first, its Tax last. */
export const TRANSACTIONS_VIEW = {
    Orders: 'orders',
    Margin: 'margin',
    Returns: 'returns',
    Products: 'products',
    Tax: 'tax',
} as const;

export type TransactionsView = (typeof TRANSACTIONS_VIEW)[keyof typeof TRANSACTIONS_VIEW];

export const TRANSACTIONS_VIEWS = Object.values(TRANSACTIONS_VIEW) as [
    TransactionsView,
    ...TransactionsView[],
];

/** What an order in the queue needs next, most urgent first, as the stat cards draw them. */
export const QUEUE_BUCKET = {
    Overdue: 'Overdue',
    AtCustoms: 'AtCustoms',
    Due: 'Due',
    InTransit: 'InTransit',
    None: 'None',
} as const;

export type QueueBucket = (typeof QUEUE_BUCKET)[keyof typeof QUEUE_BUCKET];

/** The four the queue's tiles count; None is the rest of the queue and has no tile. */
export const QUEUE_TILES = [
    QUEUE_BUCKET.Overdue,
    QUEUE_BUCKET.Due,
    QUEUE_BUCKET.InTransit,
    QUEUE_BUCKET.AtCustoms,
] as const satisfies readonly QueueBucket[];

export const QUEUE_BUCKETS = Object.values(QUEUE_BUCKET) as [QueueBucket, ...QueueBucket[]];

/** The Clients sub-tabs, one per ticket rule, in the order the tab draws them. */
export const CLIENTS_VIEW = {
    Spend: 'spend',
    Mix: 'mix',
    Inactive: 'inactive',
    DropOff: 'dropoff',
    Cycles: 'cycles',
} as const;

export type ClientsView = (typeof CLIENTS_VIEW)[keyof typeof CLIENTS_VIEW];

export const CLIENTS_VIEWS = Object.values(CLIENTS_VIEW) as [ClientsView, ...ClientsView[]];

/** Every sub-tab of either tab, which is what the address's `view` may name. */
export const REPORT_VIEWS = [...TRANSACTIONS_VIEWS, ...CLIENTS_VIEWS] as [
    TransactionsView | ClientsView,
    ...(TransactionsView | ClientsView)[],
];

/**
 * The inactive windows the screen offers (Franka, 22 Oct 2026). The default is not one of these
 * constants: it is Reports:InactiveAfterDays, which the API reports beside the list.
 */
export const INACTIVE_WINDOWS = [90, 180, 365] as const;

export const CLIENT_SIGNAL = {
    New: 'New',
    Inactive: 'Inactive',
    AwaitingPayment: 'AwaitingPayment',
    WalletCredit: 'WalletCredit',
} as const;

export type ClientSignal = (typeof CLIENT_SIGNAL)[keyof typeof CLIENT_SIGNAL];

export const RETURN_SETTLEMENT = {
    CreditNote: 'CreditNote',
    LegacyWalletRefund: 'LegacyWalletRefund',
    Unmatched: 'Unmatched',
} as const;

export type ReturnSettlement = (typeof RETURN_SETTLEMENT)[keyof typeof RETURN_SETTLEMENT];

export const RETURN_CHECK = {
    ReturnBooked: 'ReturnBooked',
    NoReturnParcel: 'NoReturnParcel',
    ArrivedNotMarked: 'ArrivedNotMarked',
    ReadyToCredit: 'ReadyToCredit',
    ReceivedWithoutParcel: 'ReceivedWithoutParcel',
    ParcelNotArrived: 'ParcelNotArrived',
} as const;

export type ReturnCheck = (typeof RETURN_CHECK)[keyof typeof RETURN_CHECK];

/** The two checks where the item and its parcel disagree, which someone has to look at. */
export const MISMATCH_CHECKS: readonly ReturnCheck[] = [
    RETURN_CHECK.ArrivedNotMarked,
    RETURN_CHECK.ParcelNotArrived,
];

/**
 * The order statuses the queue's API accepts: a cancelled or deleted order is never in it. The
 * URL is checked against these, so a hand-edited link falls back instead of failing the view.
 */
export const QUEUE_ORDER_STATUSES = [
    'InProgress',
    'Completed',
    'Placed',
] as const satisfies readonly OrderStatus[];

export type QueueOrderStatus = (typeof QUEUE_ORDER_STATUSES)[number];

/** Every payment status, as the API names them. */
export const QUEUE_PAYMENT_STATUSES = [
    'NotPaid',
    'DepositUnpaid',
    'DepositPaid',
    'PartiallyPaid',
    'FullyPaid',
    'Refunded',
] as const satisfies readonly PaymentStatus[];

/** What Brand intelligence can say about a brand in a quarter, as the API names it (ALS-91). */
export const BRAND_REPORT_STATE = {
    Report: 'Report',
    BeforeData: 'BeforeData',
    NotStocked: 'NotStocked',
    NotEnoughActivity: 'NotEnoughActivity',
} as const;

export type BrandReportState = (typeof BRAND_REPORT_STATE)[keyof typeof BRAND_REPORT_STATE];

/** Why a Brand intelligence tile has no figure: it shows a dash and this reason, never a zero. */
export const BRAND_FIGURE_GAP = {
    NoPiecesShown: 'NoPiecesShown',
    NoViews: 'NoViews',
    NoPurchases: 'NoPurchases',
    NoClientSpend: 'NoClientSpend',
    RefundsExceedSpend: 'RefundsExceedSpend',
    TooFewClients: 'TooFewClients',
} as const;

export type BrandFigureGap = (typeof BRAND_FIGURE_GAP)[keyof typeof BRAND_FIGURE_GAP];

/** App usage's windows in days, the segment control's choices (ALS-160). */
export const APP_USAGE_WINDOWS = [7, 30, 90] as const;

export type AppUsageDays = (typeof APP_USAGE_WINDOWS)[number];

export const DEFAULT_APP_USAGE_DAYS: AppUsageDays = 30;

export const isAppUsageDays = (days: number): days is AppUsageDays =>
    (APP_USAGE_WINDOWS as readonly number[]).includes(days);

/** Where a person's last-used version stands on their platform's store release list. */
export const VERSION_STANDING = {
    Latest: 'Latest',
    OneBehind: 'OneBehind',
    WellBehind: 'WellBehind',
    Unlisted: 'Unlisted',
} as const;

export type VersionStanding = (typeof VERSION_STANDING)[keyof typeof VERSION_STANDING];

/**
 * Worth a call's reasons, as the API names them, and nothing else. The ticket's fourth, two or
 * more expired sessions, is not among them: the app does not record an expiry (ALS-160). Not
 * opened is only someone who used the app in the 90 days before the window and stopped.
 */
export const APP_USAGE_REASON = {
    ResetPassword: 'ResetPassword',
    ReleasesBehind: 'ReleasesBehind',
    NotOpened: 'NotOpened',
} as const;

export type AppUsageReason = (typeof APP_USAGE_REASON)[keyof typeof APP_USAGE_REASON];

/** The Client heatmap's windows in weeks, the segment control's choices, longest first (ALS-88). */
export const HEATMAP_WINDOWS = [6, 4, 2] as const;

export type HeatmapWeeks = (typeof HEATMAP_WINDOWS)[number];

export const DEFAULT_HEATMAP_WEEKS: HeatmapWeeks = 6;

export const isHeatmapWeeks = (weeks: number): weeks is HeatmapWeeks =>
    (HEATMAP_WINDOWS as readonly number[]).includes(weeks);

/** A client's engagement signal, as the API names them, in the order its rules are checked. */
export const ENGAGEMENT_SIGNAL = {
    GoneQuiet: 'GoneQuiet',
    FirstInterest: 'FirstInterest',
    GoingQuiet: 'GoingQuiet',
    WarmingUp: 'WarmingUp',
    Steady: 'Steady',
} as const;

export type EngagementSignal = (typeof ENGAGEMENT_SIGNAL)[keyof typeof ENGAGEMENT_SIGNAL];

/** The two a stylist should act on, which the grid draws in the warning tone. */
export const QUIET_SIGNALS: readonly EngagementSignal[] = [
    ENGAGEMENT_SIGNAL.GoneQuiet,
    ENGAGEMENT_SIGNAL.GoingQuiet,
];
